import { useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { adminApi } from "../auth";

const COLORS = ["#E60000", "#FF5A5A", "#FFB30B", "#00A35C", "#3D7AFF", "#9C50FF"];

const AdminRevenue = () => {
  const [period, setPeriod] = useState("day");
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/revenue", { params: { period } }).then((r) => setD(r.data)); }, [period]);
  if (!d) return <p className="vd-muted">Loading…</p>;

  return (
    <div data-testid="admin-revenue-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: 28 }}>Revenue</h1>
          <p className="vd-muted">All confirmed orders.</p>
        </div>
        <select className="vd-select" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ width: "auto" }} data-testid="revenue-period-select">
          <option value="day">Daily</option>
          <option value="week">Weekly</option>
          <option value="month">Monthly</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Total Revenue" value={`£${d.total_revenue.toFixed(2)}`} testid="revenue-total" />
        <Stat label="Total Orders" value={d.total_orders} testid="revenue-orders-total" />
        <Stat label="Average Order Value" value={`£${d.average_order_value.toFixed(2)}`} testid="revenue-aov" />
      </div>

      <div className="vd-card p-5 mb-6" data-testid="revenue-time-chart">
        <p className="vd-heading font-semibold mb-3">Revenue over time ({period})</p>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={d.series}>
              <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
              <YAxis tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
              <Tooltip contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", color: "var(--text-heading)" }} />
              <Line type="monotone" dataKey="revenue" stroke="var(--primary-red)" strokeWidth={2.5} dot={{ fill: "var(--primary-red)" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="vd-card p-5" data-testid="revenue-top-products-chart">
          <p className="vd-heading font-semibold mb-3">Top products (revenue)</p>
          {d.top_products.length === 0 ? <p className="vd-muted text-sm">No sales yet.</p> : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={d.top_products.map((p) => ({ name: (p.name || "").slice(0, 20), revenue: p.revenue }))} layout="vertical">
                  <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "var(--text-muted)", fontSize: 11 }} width={130} />
                  <Tooltip contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", color: "var(--text-heading)" }} />
                  <Bar dataKey="revenue" fill="var(--primary-red)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="vd-card p-5" data-testid="revenue-category-pie">
          <p className="vd-heading font-semibold mb-3">Category distribution</p>
          {d.category_mix.length === 0 ? <p className="vd-muted text-sm">No sales yet.</p> : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={d.category_mix} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90} label>
                    {d.category_mix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", color: "var(--text-heading)" }} />
                  <Legend wrapperStyle={{ color: "var(--text-heading)", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="vd-card p-5" data-testid={testid}>
    <p className="vd-muted text-xs uppercase tracking-wider">{label}</p>
    <p className="vd-heading font-extrabold" style={{ fontSize: 26 }}>{value}</p>
  </div>
);

export default AdminRevenue;
